import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import confetti from 'canvas-confetti';

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [payment, setPayment] = useState(null);
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      toast.error('No session ID found');
      navigate('/dashboard');
      return;
    }

    pollPaymentStatus(sessionId, 0);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 5) {
      toast.error('Payment verification timed out');
      setChecking(false);
      return;
    }

    try {
      const response = await API.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPayment(response.data);
        setChecking(false);
        confetti({
          particleCount: 200,
          spread: 100,
          origin: { y: 0.5 }
        });
        toast.success('Payment successful! Welcome to Premium!');
      } else if (response.data.payment_status === 'pending') {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        toast.error('Payment failed');
        setChecking(false);
      }
    } catch (error) {
      toast.error('Failed to verify payment');
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-xl">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6">
      <div className="max-w-lg w-full text-center">
        <div className="ninja-card">
          <div className="mb-6">
            <CheckCircle className="text-green-500 w-24 h-24 mx-auto mb-4" />
            <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="success-title">Payment Successful!</h1>
            <p className="text-gray-400 text-lg">Welcome to Negotiation Ninja Premium</p>
          </div>

          <div className="bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="text-yellow-500 w-6 h-6" />
              <h2 className="font-['Outfit'] text-2xl font-bold">You now have:</h2>
            </div>
            <ul className="text-left space-y-2 text-gray-300">
              <li>✓ Unlimited AI roleplay sessions</li>
              <li>✓ Advanced script analytics</li>
              <li>✓ Export scripts to PDF</li>
              <li>✓ Priority support</li>
            </ul>
          </div>

          <Button 
            onClick={() => navigate('/dashboard')}
            className="w-full bg-green-600 hover:bg-green-500 text-lg py-6"
            data-testid="go-to-dashboard-button"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;